import React, { useState, useEffect } from 'react';
import { Progress, Typography, Card, Space, Button, message } from 'antd';
import { CheckCircleOutlined, WarningOutlined, CloudDownloadOutlined, FontSizeOutlined } from '@ant-design/icons';
import { Font } from '@react-pdf/renderer';
import { getAllFontIds, isFontCached, getFontData, storeFontData, clearFontCache } from '../../utils/fontDatabase';
import './FontLoader.scss';

const { Title, Text, Paragraph } = Typography;

const FontLoader = ({ onComplete }) => {
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentFont, setCurrentFont] = useState('');
  const [error, setError] = useState(null);
  const [loadedFonts, setLoadedFonts] = useState(0);
  const [totalFonts, setTotalFonts] = useState(0);
  const [loadingStage, setLoadingStage] = useState('preparing'); // preparing, loading, complete, error

  useEffect(() => {
    const loadFonts = async () => {
      try {
        setLoadingStage('preparing');
        // 获取所有需要加载的字体
        const fontIds = getAllFontIds();
        setTotalFonts(fontIds.length);
        
        if (fontIds.length === 0) {
          // 没有字体需要加载，直接完成
          setLoading(false);
          setLoadingStage('complete');
          setTimeout(() => onComplete(), 1000); // 短暂延迟以显示完成动画
          return;
        }

        let loaded = 0;
        setLoadingStage('loading');

        // 依次加载每个字体
        for (const fontInfo of fontIds) {
          setCurrentFont(`${fontInfo.family} (${fontInfo.weight} ${fontInfo.style})`);
          
          try {
            // 检查字体是否已缓存
            const isCached = await isFontCached(fontInfo.id);
            
            if (isCached) {
              // 从缓存加载字体
              const fontData = await getFontData(fontInfo.id);
              
              // 使用 Blob URL 注册字体
              const fontBlob = new Blob([fontData], { type: 'font/opentype' });
              const fontUrl = URL.createObjectURL(fontBlob);
              
              Font.register({
                family: fontInfo.family,
                src: fontUrl,
                fontWeight: fontInfo.weight,
                fontStyle: fontInfo.style,
              });
              
              console.log(`Loaded font from cache: ${fontInfo.family}`);
            } else {
              // 从网络加载字体
              console.log(`Fetching font from: ${fontInfo.src}`);
              let fontData;
              try {
                const response = await fetch(fontInfo.src);
                
                if (!response.ok) {
                  throw new Error(`Failed to fetch font: ${response.statusText} (${response.status})`);
                }
                
                // 获取字体数据
                fontData = await response.arrayBuffer();
                console.log(`Successfully fetched font: ${fontInfo.family}, size: ${fontData.byteLength} bytes`);
              } catch (fetchError) {
                console.error(`Error fetching font ${fontInfo.family}:`, fetchError);
                throw fetchError;
              }
              
              // 使用 Blob URL 注册字体
              const fontBlob = new Blob([fontData], { type: 'font/opentype' });
              const fontUrl = URL.createObjectURL(fontBlob);
              
              Font.register({
                family: fontInfo.family,
                src: fontUrl,
                fontWeight: fontInfo.weight,
                fontStyle: fontInfo.style,
              });
              
              // 缓存字体数据
              await storeFontData(
                fontInfo.id,
                fontInfo.family,
                fontInfo.style,
                fontInfo.weight,
                fontData
              );
              
              console.log(`Loaded and cached font: ${fontInfo.family}`);
            }
            
            // 更新加载进度
            loaded++;
            setLoadedFonts(loaded);
            setProgress(Math.round((loaded / fontIds.length) * 100));
            
          } catch (fontError) {
            console.error(`Error loading font ${fontInfo.family}:`, fontError);
            // 继续加载其他字体，不中断流程
          }
        }
        
        // 所有字体加载完成
        setLoading(false);
        setLoadingStage('complete');
        setTimeout(() => onComplete(), 1500); // 短暂延迟以显示完成动画
        
      } catch (err) {
        console.error('Font loading error:', err);
        setError(err.message);
        setLoading(false);
        setLoadingStage('error');
      }
    };

    loadFonts();
  }, [onComplete]);

  // 渲染加载阶段的图标
  const renderStageIcon = () => {
    if (loadingStage === 'preparing' || loadingStage === 'loading') {
      return <div className="custom-loader"></div>;
    }
    
    switch (loadingStage) {
      case 'complete':
        return <CheckCircleOutlined className="stage-icon complete" />;
      case 'error':
        return <WarningOutlined className="stage-icon error" />;
      default:
        return <FontSizeOutlined className="stage-icon" />;
    }
  };

  return (
    <div className="font-loader-container">
      <div className="font-loader-background"></div>
      
      <Card 
        className="font-loader-card"
        variant="borderless"
      >
        <div className="font-loader-header">
          <div className="font-loader-logo">
            <FontSizeOutlined className="logo-icon" />
            <span>外刊双语精读</span>
          </div>
        </div>

        <div className="font-loader-content">
          {renderStageIcon()}
          
          <Title level={3} className="font-loader-title">
            {loadingStage === 'preparing' && '准备资源中...'}
            {loadingStage === 'loading' && '加载应用资源'}
            {loadingStage === 'complete' && '加载完成'}
            {loadingStage === 'error' && '加载出错'}
          </Title>
          
          {loading ? (
            <>
              <div className="font-loader-progress-container">
                <Progress 
                  percent={progress} 
                  status="active" 
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                  className="font-loader-progress"
                />
                <Text className="font-loader-progress-text">
                  {loadedFonts}/{totalFonts} 字体已加载
                </Text>
              </div>
              
              <div className="font-loader-info">
                <Text className="font-loader-current-font">{currentFont}</Text>
              </div>
              
              <Paragraph className="font-loader-tip">
                首次加载需要下载字体资源，请稍候...
                <br />
                后续访问将直接从本地缓存加载，无需等待
              </Paragraph>
            </>
          ) : error ? (
            <div className="font-loader-error">
              <Paragraph type="danger" className="font-loader-error-message">
                {error}
              </Paragraph>
              <Paragraph className="font-loader-tip">
                应用将使用系统默认字体继续，部分文字可能无法正常显示
              </Paragraph>
              <div className="font-loader-actions">
                <Button 
                  type="primary" 
                  danger 
                  icon={<CloudDownloadOutlined />}
                  onClick={async () => {
                    try {
                      await clearFontCache();
                      message.success('字体缓存已清除，正在重新加载...');
                      window.location.reload();
                    } catch (e) {
                      message.error('清除缓存失败: ' + e.message);
                    }
                  }}
                >
                  清除缓存并重试
                </Button>
                <Button 
                  type="default"
                  onClick={() => onComplete()}
                >
                  继续使用
                </Button>
              </div>
            </div>
          ) : (
            <div className="font-loader-complete">
              <Progress percent={100} status="success" className="font-loader-progress" />
              <Paragraph className="font-loader-tip success">
                所有资源加载完成，正在进入应用...
              </Paragraph>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default FontLoader;